"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  convertMarkdown,
  htmlDocument,
  MAX_MARKDOWN_LENGTH,
} from "@/lib/tool-kit/developer/markdown-to-html";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const MARKDOWN_TO_HTML_STRINGS = {
  inputLabel: "Markdown",
  inputHint:
    "Converts as you type, entirely in your browser — nothing is uploaded.",
  sample:
    "# Field notes\n\nResults were **significant** (*p* < .05). See the [CommonMark spec](https://spec.commonmark.org/) for the full syntax.\n\n- Sample size: 48\n- Effect size: `d = 0.62`\n  - medium, by Cohen's benchmarks\n\n> Raw HTML such as <b>this</b> is escaped unless you allow it.\n\n| Group | Mean | SD |\n| :--- | ---: | ---: |\n| Control | 4.1 | 0.8 |\n| Treatment | 5.3 | 0.9 |\n\n~~Draft~~ Final version.\n",
  loadSample: "Load the example",
  clear: "Clear",
  rawHtmlLabel: "Raw HTML in your Markdown",
  rawHtmlOptions: {
    escape: "Escape it (show tags as text)",
    allow: "Allow a safe subset",
  },
  gfmLabel: "GitHub extensions: tables, strikethrough, bare-URL links",
  results: "HTML",
  summary: "{input} of Markdown became {output} of HTML.",
  characterCount: "one: {count} character|other: {count} characters",
  sourceLabel: "HTML source",
  copyHtml: "Copy HTML",
  copied: "Copied!",
  download: "Download .html",
  downloadFilename: "converted.html",
  documentTitle: "Converted Markdown",
  previewHeading: "Preview",
  previewHint:
    "Sanitised before display. Links open in a new tab; images load from their own addresses.",
  emptyPrompt: "Type or paste Markdown above to see its HTML.",
  failed:
    "This document nests too deeply to convert. Flatten the deepest quotes or emphasis and try again.",
  truncated: "Only the first {limit} characters are converted.",
} as const;

type RawHtmlMode = "escape" | "allow";
const RAW_HTML_MODES: readonly RawHtmlMode[] = ["escape", "allow"];

/**
 * The preview's own type scale; the HTML itself carries no classes. The typography
 * plugin decorates code with backticks and quotes with quotation marks, and sets table
 * cells' alignment — none of which is in the HTML, so all three are undone here.
 */
const PREVIEW =
  "prose prose-neutral dark:prose-invert max-w-none overflow-x-auto rounded-lg border border-border bg-card p-4 sm:p-5 prose-code:before:content-none prose-code:after:content-none prose-blockquote:not-italic [&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none [&_[align=center]]:text-center [&_[align=left]]:text-left [&_[align=right]]:text-right";

export default function MarkdownToHtmlWidget() {
  const strings = useToolUi(MARKDOWN_TO_HTML_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [source, setSource] = useState<string>(strings.sample);
  const [rawHtml, setRawHtml] = useState<RawHtmlMode>("escape");
  const [gfm, setGfm] = useState(true);
  const deferred = useDeferredValue(source);

  const result = useMemo(
    () =>
      deferred.trim()
        ? convertMarkdown(deferred, { gfm, allowHtml: rawHtml === "allow" })
        : null,
    [deferred, gfm, rawHtml]
  );

  const count = (n: number) =>
    tPlural(strings.characterCount, locale, n, { count: fmt(n) }).trim();

  const modeOptions = RAW_HTML_MODES.map((mode) => ({
    value: mode,
    label: strings.rawHtmlOptions[mode],
  }));

  function renderResult() {
    if (!source.trim()) {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (result === null) {
      return <ErrorNotice>{strings.failed}</ErrorNotice>;
    }
    return (
      <>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className={HINT}>
          {t(strings.summary, {
            input: count(deferred.length),
            output: count(result.html.length),
          })}
        </p>
        {deferred.length > MAX_MARKDOWN_LENGTH ? (
          <Notice>
            {t(strings.truncated, { limit: fmt(MAX_MARKDOWN_LENGTH) })}
          </Notice>
        ) : null}
        <div className="space-y-2">
          <label className={LABEL} htmlFor="mdh-output">
            {strings.sourceLabel}
          </label>
          <textarea
            className={TEXTAREA}
            id="mdh-output"
            readOnly
            rows={12}
            spellCheck={false}
            value={result.html}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyHtml}
            text={result.html}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              downloadText(
                strings.downloadFilename,
                htmlDocument(result.html, strings.documentTitle, locale),
                "text/html;charset=utf-8"
              )
            }
            type="button"
          >
            {strings.download}
          </button>
        </div>
        <h3 className={WIDGET_H3}>{strings.previewHeading}</h3>
        <p className={HINT}>{strings.previewHint}</p>
        <div
          className={PREVIEW}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitizeHtml rewrote this from scratch — allowlisted tags and attributes only, every value and text run escaped, URLs limited to http(s), mailto and relative.
          dangerouslySetInnerHTML={{ __html: result.preview }}
        />
      </>
    );
  }

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="mdh-input"
        label={strings.inputLabel}
        onChange={setSource}
        rows={14}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource(strings.sample)}
          type="button"
        >
          {strings.loadSample}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={source.length === 0}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className={`${CARD} grid gap-4 sm:grid-cols-2`}>
        <PillGroup
          label={strings.rawHtmlLabel}
          name="mdh-raw-html"
          onChange={setRawHtml}
          options={modeOptions}
          value={rawHtml}
        />
        <div className="self-end">
          <Checkbox checked={gfm} label={strings.gfmLabel} onChange={setGfm} />
        </div>
      </div>
      <div className="space-y-4">{renderResult()}</div>
    </section>
  );
}
